import React, { useState, useEffect } from 'react';
import './ColorPicker.css';

interface ColorPickerProps {
  value?: string;
  onChange?: (color: string) => void;
  onSave?: (color: string, colorType: string) => Promise<boolean>;
  presetColors?: string[];
  label?: string;
  className?: string;
  colorType?: 'primary' | 'secondary' | 'tertiary';
  isLoading?: boolean;
}

const ColorPicker: React.FC<ColorPickerProps> = ({
  value = '#8090FF',
  onChange,
  onSave,
  label,
  className = '',
  colorType = 'primary',
  isLoading = false
}) => {
  const [selectedColor, setSelectedColor] = useState(value);
  const [isSaving, setIsSaving] = useState(false);

  // Update local state when value prop changes
  useEffect(() => {
    setSelectedColor(value);
  }, [value]);

  const getLabel = () => {
    if (label) return label;
    switch (colorType) {
      case 'primary':
        return 'Primary Colour';
      case 'secondary':
        return 'Secondary Colour';
      case 'tertiary':
        return 'Tertiary Colour';
      default:
        return 'Choose color';
    }
  };

  const getSettingType = () => {
    switch (colorType) {
      case 'primary':
        return 'primaryColourStatus';
      case 'secondary':
        return 'secondaryColourStatus';
      case 'tertiary':
        return 'tertiaryColourStatus';
      default:
        return 'primaryColourStatus';
    }
  };

  const handleColorChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = event.target.value;
    setSelectedColor(newColor);
    
    // Call onChange immediately for local state update
    onChange?.(newColor);
    
    // Save to API if onSave function is provided
    if (onSave) {
      setIsSaving(true);
      try {
        const success = await onSave(newColor, getSettingType());
        if (!success) {
          // Revert to previous value if save failed
          setSelectedColor(value);
          onChange?.(value);
        }
      } catch (error) {
        console.error('Error saving color:', error);
        // Revert to previous value on error
        setSelectedColor(value);
        onChange?.(value);
      } finally {
        setIsSaving(false);
      }
    }
  };

  return (
    <div className={`color-picker ${className}`}>
      <label className="color-picker-label">{getLabel()}</label>
      
      <div className="color-input-container">
        <input
          type="color"
          value={selectedColor}
          onChange={handleColorChange}
          className="color-input"
          title="Choose color"
          disabled={isSaving || isLoading}
        />
        
        <div className="selected-color">
          <span className="color-value">
            {selectedColor.toUpperCase()}
            {(isSaving || isLoading) && <span style={{marginLeft: '8px', color: '#6b7280'}}>Saving...</span>}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ColorPicker;